import {} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import Debug from 'debug'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { UseMutationExecute, UseMutationState, UseQueryExecute, useMutation, useQuery } from 'urql'
import { graphql } from '../gql'
import { BulkPaymentStepperQuery, CreatePaymentInput, CreateVendorPaymentInput, Exact, RfqStatus } from '../gql/graphql'
import { CreatePaymentsMutation } from '../gql/operations'

const log = Debug('ela:BulkPaymentStepperContextProvider')
log.log = console.info.bind(console)

export type TBulkPaymentStepperContext = {
  fetching: boolean
  error?: Error
  refetch: UseQueryExecute
  rfqs?: BulkPaymentStepperQuery['Rfqs']
  activeStep: number
  setActiveStep: (activeStep: number) => void
  vendorId?: number
  setVendorId: (vendorId: number) => void
  paymentTotal?: number
  setPaymentTotal: (paymentTotal: number) => void
  dateReceived: Dayjs
  setDateReceived: (dateReceived: Dayjs) => void
  vendors?: BulkPaymentStepperQuery['Rfqs'][0]['vendor'][]
  vendor?: BulkPaymentStepperQuery['Rfqs'][0]['vendor']
  createPayments: UseMutationExecute<
    CreatePaymentsMutation,
    Exact<{
      payments: CreatePaymentInput | CreatePaymentInput[]
      vendorPayments: CreateVendorPaymentInput | CreateVendorPaymentInput[]
    }>
  >
  createPaymentsResult: UseMutationState<
    CreatePaymentsMutation,
    Exact<{
      payments: CreatePaymentInput | CreatePaymentInput[]
      vendorPayments: CreateVendorPaymentInput | CreateVendorPaymentInput[]
    }>
  >
}

export const BulkPaymentStepperContext = createContext<TBulkPaymentStepperContext | undefined>(undefined)

const createBulkPaymentsMutation = graphql(/* GraphQL */ `
  mutation CreatePayments($payments: [CreatePaymentInput!]!, $vendorPayments: [CreateVendorPaymentInput!]!) {
    CreatePayments(payments: $payments, vendorPayments: $vendorPayments)
  }
`)

export function BulkPaymentStepperContextProvider({ children }: { children: React.ReactNode }) {
  const [activeStep, setActiveStep] = useState(0)
  const [vendorId, setVendorId] = useState<number | undefined>()
  const [paymentTotal, setPaymentTotal] = useState<number | undefined>()

  const [createPaymentsResult, createPayments] = useMutation(createBulkPaymentsMutation)

  const [pageQueryResult, refreshPageQuery] = useQuery({
    query: graphql(/* GraphQL */ `
      query BulkPaymentStepper($filters: ListRfqsFilterInput) {
        Rfqs(filters: $filters) {
          id
          status
          note
          project {
            name
          }
          vendor {
            id
            name
            website
            outstandingCommission
          }
          client {
            name
            website
          }
          quote {
            quoteNumber
            purchaseOrder {
              id
              amount
              commissionPercentage
              outstandingCommission
              poNumber
            }
          }
        }
      }
  `),
    variables: {
      filters: {
        statuses: [RfqStatus.Purchased, RfqStatus.Paid],
      },
    },
  })

  const rfqs = pageQueryResult.data?.Rfqs

  const vendors = useMemo<BulkPaymentStepperQuery['Rfqs'][0]['vendor'][]>(() => {
    if (!rfqs) return []
    return rfqs.reduce(
      (acc, rfq) => {
        if (!rfq.vendor) return acc
        if (acc.find((v) => v.id === rfq.vendor.id)) return acc
        return [...acc, rfq.vendor]
      },
      [] as BulkPaymentStepperQuery['Rfqs'][0]['vendor'][],
    )
  }, [rfqs])

  const vendor = useMemo(() => {
    if (!vendors) return
    if (!vendorId) return
    return vendors.find((v) => v.id === vendorId)
  }, [vendors, vendorId])

  useEffect(() => {
    if (createPaymentsResult.data && !createPaymentsResult.fetching && !createPaymentsResult.error) {
      setActiveStep(4)
    }
  }, [createPaymentsResult.data, createPaymentsResult.fetching, createPaymentsResult.error])

  const value: TBulkPaymentStepperContext = {
    fetching: pageQueryResult.fetching,
    rfqs: pageQueryResult.data?.Rfqs,
    error: pageQueryResult.error,
    refetch: refreshPageQuery,
    activeStep,
    setActiveStep,
    vendors,
    vendorId,
    vendor,
    setVendorId,
    paymentTotal,
    setPaymentTotal,
    dateReceived: dayjs(),
    setDateReceived: () => {},
    createPayments,
    createPaymentsResult,
  }

  useEffect(() => {
    if (value.error) {
      log('Error loading query', value.error)
    }
  }, [value.error])

  return <BulkPaymentStepperContext.Provider value={value}>{children}</BulkPaymentStepperContext.Provider>
}

export function useBulkPaymentStepperContext() {
  const context = useContext(BulkPaymentStepperContext)
  if (!context) {
    throw new Error('useBulkPaymentStepperContext must be used within an BulkPaymentStepperContext')
  }
  return context
}
